import type { CatalogSection, Country } from './types.js';

/**
 * What a deployment may sell — the two OPTIONAL `features` switches resolved,
 * so the API, the web and the prerender cannot each spell the fallback their
 * own way. Both take the narrowest shape (a full `CountryConfig`, a
 * `PublicCountryConfig` and a test literal all fit).
 */

interface HasPublicSourceData {
  readonly features: { readonly publicSourceData?: boolean };
}

interface HasOutcomeWall {
  readonly code: Country;
  readonly features: { readonly outcomeWall?: boolean };
}

/**
 * Does the source publish the lot's documents, buyer contact and notice page
 * openly (`features.publicSourceData`)? Then they ship to every viewer and are
 * not sold. Absent → `false`: Uzbekistan and Kazakhstan keep their paywall.
 */
export function sourceDataIsPublic(cfg: HasPublicSourceData): boolean {
  return cfg.features.publicSourceData === true;
}

/**
 * Is a lot's single award (winner, company id, final price) walled behind Pro
 * (`features.outcomeWall`)? Absent → the rule that predates the field:
 * Uzbekistan only (owner, 2026-10-07; Kazakhstan's goszakup publishes it).
 */
export function outcomeWallOn(cfg: HasOutcomeWall): boolean {
  return cfg.features.outcomeWall ?? cfg.code === 'uz';
}

interface HasExpiringContracts {
  readonly features: { readonly expiringContracts?: boolean };
}

/**
 * Does this deployment offer «Próximos vencimientos» (`features.expiringContracts`)?
 * Absent → `false`: Uzbekistan and Kazakhstan have no contract-duration data and
 * get no page, no sidebar row and no endpoint.
 */
export function expiringContractsOn(cfg: HasExpiringContracts): boolean {
  return cfg.features.expiringContracts === true;
}

interface HasKindTabs {
  readonly features: { readonly priceIntel: boolean; readonly kindTabs?: boolean };
}

/**
 * Does the catalogue show its «goods / works / services» tabs
 * (`features.kindTabs`)? Absent → the rule that predates the field: they follow
 * `priceIntel` (Uzbekistan and Kazakhstan). Spain opts in explicitly.
 */
export function kindTabsOn(cfg: HasKindTabs): boolean {
  return cfg.features.kindTabs ?? cfg.features.priceIntel;
}

interface HasSections {
  readonly features: { readonly sections?: readonly CatalogSection[] };
}

/**
 * Does this deployment serve the catalogue's `ifi` / `supply` section at all
 * (`features.sections`)? Absent → yes, both (UZ, KZ: the page renders and its
 * sidebar row self-hides on an empty count). Spain lists none.
 */
export function catalogSectionServed(cfg: HasSections, section: CatalogSection): boolean {
  return cfg.features.sections?.includes(section) ?? true;
}

/**
 * How many comparable lots must carry a market figure before it is shown free
 * under `features.freeBidderMedian` (Spain): the median bidder count on the
 * teaser, the «¿Por cuánto se gana? … en N lotes parecidos» line. Below it,
 * nothing — a «median» of three lots is one lot's number with a label on it.
 * The API and the web read this one value.
 */
export const FREE_MARKET_MIN_LOTS = 5;

interface HasFreeBidderMedian {
  readonly features: { readonly freeBidderMedian?: boolean };
}

/** `features.freeBidderMedian` resolved — absent → `false` (UZ, KZ). */
export function freeBidderMedianOn(cfg: HasFreeBidderMedian): boolean {
  return cfg.features.freeBidderMedian === true;
}
